import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import jwt from 'jsonwebtoken';
import moment from 'moment';
import { toast } from 'react-toastify';
import { ticketListReport, ticketCetagoryByCompany } from './../../redux/actions/reportAction';
import { logoutUser } from '../../redux/actions/user';
import { storesList } from '../../redux/actions/store';
import { ReportsProps } from '../../interfaces/reports';
import Select from 'react-select';

// TanStack Table v8+
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';

// UI Components
import Topbar from '../topbar/topbar';
import Sidebar from '../sidebar/sidebar';
import Footer from '../footer/footer';
import config from '../config/setting';

// 👇 Helper to flatten nested objects while preserving parent-child structure
type FlattenedField = {
  parent: string | null;
  key: string;
  label: string;
};

const flattenNestedFields = (obj: any, parent: string | null = null): FlattenedField[] => {
  let result: FlattenedField[] = [];

  Object.keys(obj).forEach(key => {
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      // Recurse into nested object
      result = result.concat(flattenNestedFields(obj[key], key));
    } else {
      result.push({
        parent,
        key: parent ? `${parent} → ${key}` : key,
        label: key,
      });
    }
  });

  return result;
};

// 👇 Helper to flatten row data for accessor compatibility
const flattenRowData = (row: any) => {
  const { brand, ticketsCount, ...rest } = row;
  const flattened: any = { brand, ticketsCount };

  const flatten = (obj: any, prefix: string | null = null) => {
    Object.keys(obj).forEach(key => {
      const fullKey = prefix ? `${prefix} → ${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        flatten(obj[key], key); // Use direct child key as next prefix
      } else {
        flattened[fullKey] = obj[key];
      }
    });
  };

  flatten(rest);
  return flattened;
};

const Reports: React.FC<ReportsProps> = ({ ticket_report, ticket_cetagory_by_company, ticketListReport, logoutUser, ticketCetagoryByCompany }) => {
  const history = useHistory();

  // Form State
  const [reportType, setReportType] = useState('');
  const [storeType, setStoreType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoad, setIsLoad] = useState(false);
  const [selectedStores, setSelectedStore] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasGeneratedReport, setHasGeneratedReport] = useState(false);

  const maxDate = moment().format('YYYY-MM-DDTHH:mm');
  const dispatch = useDispatch();
  const { storeList } = useSelector((state: any) => state.store);
  const { error: reportError } = useSelector((state: any) => state.report);

  useEffect(() => {
    dispatch(storesList());
  }, [dispatch]);

  useEffect(() => {
    if (reportError) {
      setIsLoad(false);
    }
  }, [reportError]);

  // Format time difference helper
  const formatTimeDiff = (start: string, end: string) => {
    if (!start || !end) return 'N/A';
    const diffMs = new Date(end).getTime() - new Date(start).getTime();
    if (diffMs < 0) return 'Invalid';
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return [
      days > 0 && `${days} day${days !== 1 ? 's' : ''}`,
      hours > 0 && `${hours} hour${hours !== 1 ? 's' : ''}`,
      minutes > 0 && `${minutes} min`,
    ]
      .filter(Boolean)
      .join(' ') || '0 min';
  };

  // Auth check
  useEffect(() => {
    document.title = 'SimplexServices | Reports';
    const roleId = sessionStorage.getItem("role");
    setIsAdmin(roleId === '1' || roleId === 'admin');

    if (!sessionStorage.token) {
      history.push('/');
      return;
    }

    jwt.verify(sessionStorage.token, config.secretKey, (err: any) => {
      if (err) {
        logoutUser();
        history.push('/');
      }
    });
  }, [history, logoutUser]);

  // Clear loading when data arrives + mark report as ready
  useEffect(() => {
    if (ticket_report) {
      setIsLoad(false);
      setHasGeneratedReport(true);
    }
  }, [ticket_report]);

  useEffect(() => {
    if (ticket_cetagory_by_company) {
      setIsLoad(false);
      setHasGeneratedReport(true);
    }
  }, [ticket_cetagory_by_company]);

  // 👇 Clear report view when inputs change
  useEffect(() => {
    setHasGeneratedReport(false);
  }, [reportType, startDate, endDate, selectedStores, storeType]);

  // Input handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => setReportType(e.target.value);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'startDate') setStartDate(value);
    if (name === 'endDate') setEndDate(value);
  };

  // Validation
  const validateTimeRange = () => {
    if (!startDate || !endDate || startDate >= endDate) {
      toast.error('End date must be after start date');
      return false;
    }

    const diffInDays = (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24);
    if (diffInDays > 31) {
      toast.error('Date range cannot exceed 31 days.');
      return false;
    }
    return true;
  };

  // 👇 CONDITIONAL TABLE DATA — with flattened nested objects
  const currentTableData = useMemo(() => {
    if (reportType === 'ticketsByCategoryAndStore') {
      if (!ticket_cetagory_by_company || ticket_cetagory_by_company.length === 0) return [];
      return ticket_cetagory_by_company.map(flattenRowData);
    }
    return ticket_report || [];
  }, [reportType, ticket_report, ticket_cetagory_by_company]);

  // 👇 DYNAMIC COLUMNS with GROUPING for "ticketsByCategoryAndStore"
  const dynamicColumns = useMemo(() => {
    if (!ticket_cetagory_by_company || ticket_cetagory_by_company.length === 0 || reportType !== 'ticketsByCategoryAndStore') {
      return [];
    }

    // Collect all fields with parent info
    const allFields = new Map<string, { parent: string | null; label: string; accessorKey: string }>();

    ticket_cetagory_by_company.forEach((row: any) => {
      const { brand, ticketsCount, ...rest } = row;
      const fields = flattenNestedFields(rest);

      fields.forEach(field => {
        allFields.set(field.key, {
          parent: field.parent,
          label: field.label,
          accessorKey: field.key,
        });
      });
    });

    // Group by parent
    const parentGroups = new Map<string | null, any[]>();
    allFields.forEach((field, key) => {
      if (!parentGroups.has(field.parent)) {
        parentGroups.set(field.parent, []);
      }
      parentGroups.get(field.parent)!.push({
        header: field.label,
        accessorKey: field.accessorKey,
        size: 120,
        cell: (info: any) => info.getValue() || 0,
      });
    });

    // Build final columns
    const columns: any[] = [
      { header: 'Brand / Store', accessorKey: 'brand', size: 150 },
      { header: 'Total Tickets', accessorKey: 'ticketsCount', size: 120 },
    ];

    parentGroups.forEach((childColumns, parent) => {
      if (parent === null) {
        // No parent — add as top-level columns
        columns.push(...childColumns);
      } else {
        // Add as grouped column
        columns.push({
          header: parent,
          columns: childColumns,
        });
      }
    });

    return columns;
  }, [ticket_cetagory_by_company, reportType]);

  // 👇 CONDITIONAL COLUMNS
  const currentColumns = useMemo(() => {
    if (reportType === 'ticketsByCategoryAndStore') {
      return dynamicColumns.length > 0 ? dynamicColumns : [
        { header: 'Brand', accessorKey: 'brand' },
        { header: 'Tickets Count', accessorKey: 'ticketsCount' },
      ];
    }

    // Static columns for "tickets"
    return [
      { header: 'Id', accessorKey: 'id', size: 70 },
      {
        header: 'Store',
        accessorFn: (row: any) => row.store?.name || '-',
        size: 100
      },
      { header: 'Subject', accessorKey: 'subject', size: 150 },
      {
        header: 'Status',
        accessorFn: (row: any) => row.ticket_status_code?.ticket_status_description || '-',
        size: 120
      },
      {
        header: 'Priority',
        accessorFn: (row: any) => row.ticket_priority_code?.ticket_priority_description || '-',
        size: 120
      },
      {
        header: 'Received Time',
        accessorKey: 'recieved_time',
        size: 160,
        cell: (info: any) => {
          const val = info.getValue();
          if (!val) return 'N/A';
          const date = moment(val);
          return date.isValid() ? date.local().format('MMM Do YYYY | hh:mm:ss a') : 'N/A';
        },
      },
      {
        header: 'Response Time',
        accessorKey: 'assigned_time',
        size: 160,
        cell: (info: any) => {
          const val = info.getValue();
          if (!val) return 'N/A';
          const date = moment(val);
          return date.isValid() ? date.local().format('MMM Do YYYY | hh:mm:ss a') : 'N/A';
        },
      },
      {
        header: 'Response Within',
        accessorFn: (row: any) => formatTimeDiff(row.recieved_time, row.assigned_time),
        size: 160,
      },
      {
        header: 'Resolved Time',
        accessorKey: 'resolved_time',
        size: 160,
        cell: (info: any) => {
          const val = info.getValue();
          if (!val) return 'N/A';
          const date = moment(val);
          return date.isValid() ? date.local().format('MMM Do YYYY | hh:mm:ss a') : 'N/A';
        },
      },
      {
        header: 'Resolved Within',
        accessorFn: (row: any) => formatTimeDiff(row.recieved_time, row.resolved_time),
        size: 160,
      },
      {
        header: 'Requester',
        accessorFn: (row: any) => row.requester_id?.name || '-',
        size: 120
      },
      {
        header: 'Assignee',
        accessorFn: (row: any) => row.assignee_id?.name || '-',
        size: 120
      },
      {
        header: 'Response/Resolve Time Meet',
        accessorFn: (row: any) => {
          if (!row.recieved_time || !row.assigned_time || !row.resolved_time) return '-';
          const received = new Date(row.recieved_time).getTime();
          const assigned = new Date(row.assigned_time).getTime();
          const resolved = new Date(row.resolved_time).getTime();
          const responseLimit = (row.ticket_priority_code?.response_time ?? 0) * 60 * 1000;
          const resolutionLimit = (row.ticket_priority_code?.resolution_time ?? 0) * 60 * 1000;
          const responseMet = assigned - received <= responseLimit;
          const resolutionMet = resolved - received <= resolutionLimit;
          return responseMet && resolutionMet ? 'Yes' : 'No';
        },
        size: 160,
      },
    ];
  }, [reportType, ticket_report, ticket_cetagory_by_company, dynamicColumns]);

  // 👇 TanStack Table Instance
  const table = useReactTable({
    data: currentTableData,
    columns: currentColumns,
    initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    getPageCount,
    setPageIndex,
    setPageSize,
    getState,
    previousPage,
    nextPage,
    getCanPreviousPage,
    getCanNextPage,
  } = table;

  const { pageIndex, pageSize } = getState().pagination;

  // Report Generator
  const reportGenerator = useCallback(() => {
    if (!validateTimeRange()) return;

    if (reportType === 'ticketsByCategoryAndStore' && !isAdmin) {
      toast.error('You do not have permission to generate this report.');
      return;
    }

    setIsLoad(true);
    setHasGeneratedReport(false);
    setPageIndex(0);

    const data = {
      start: moment(startDate).utc(false).format('YYYY-MM-DD HH:mm'),
      end: moment(endDate).utc(false).format('YYYY-MM-DD HH:mm'),
      store_id: selectedStores.length > 0 ? selectedStores : [],
    };

    if (reportType === 'tickets') {
      ticketListReport(data);
    } else if (reportType === 'ticketsByCategoryAndStore') {
      if (selectedStores.length === 0) {
        toast.error('Please select at least one company.');
        setIsLoad(false);
        return;
      }
      ticketCetagoryByCompany(data);
    }
  }, [
    startDate,
    endDate,
    reportType,
    selectedStores,
    isAdmin,
    ticketListReport,
    ticketCetagoryByCompany,
    setPageIndex,
  ]);

  // CSV Export — Updated to handle both report types correctly
  const downloadCSV = () => {
    if (!currentTableData?.length) {
      toast.error("No data to export");
      return;
    }

    // 🔹 Helper: flatten columns recursively
    const flattenColumns = (cols: any[]): any[] =>
      cols.flatMap((col) =>
        "columns" in col && Array.isArray(col.columns)
          ? flattenColumns(col.columns)
          : [col]
      );

    // 🔹 Local helper to trigger download
    const triggerDownload = (csvRows: string[], filename: string) => {
      const BOM = "\uFEFF"; // UTF-8 BOM so Excel reads it correctly
      const blob = new Blob([BOM + csvRows.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    };

    let csvRows: string[] = [];

    // ==============================
    // CASE 1: Tickets → flat export
    // ==============================
    if (reportType === "tickets") {
      const leafCols = flattenColumns(currentColumns);
      const headers = leafCols.map((col) => col.header);

      csvRows = [
        headers.map((h) => `"${String(h).replace(/"/g, '""')}"`).join(","),

        ...currentTableData.map((row: any) =>
          leafCols
            .map((col) => {
              let value: any = "";
              if (col.accessorFn) {
                value = col.accessorFn(row);
              } else if (col.accessorKey) {
                value = row[col.accessorKey] ?? "";
              }
              if (typeof value === "string") {
                value = value.replace(/"/g, '""');
              }
              return `"${String(value)}"`;
            })
            .join(",")
        ),
      ];

      triggerDownload(csvRows, "ticket_report.csv");
      return;
    }

    // ==========================================
    // CASE 2: Category + Subcategory → dynamic
    // ==========================================
    if (reportType === "ticketsByCategoryAndStore") {
      const leafCols = flattenColumns(currentColumns);

      // Headers: replace arrow → with single quote '
      const headers = leafCols.map(
        (col) => (col.accessorKey || col.header).replace(/→/g, "'")
      );

      csvRows = [
        headers.map((h) => `"${String(h).replace(/"/g, '""')}"`).join(","),

        ...currentTableData.map((row: any) =>
          leafCols
            .map((col) => {
              let value: any = "";
              if (col.accessorFn) {
                value = col.accessorFn(row);
              } else if (col.accessorKey) {
                value = row[col.accessorKey] ?? 0; // counts default to 0
              }
              if (typeof value === "string") {
                value = value.replace(/"/g, '""');
              }
              return `"${String(value)}"`;
            })
            .join(",")
        ),
      ];

      triggerDownload(csvRows, "category_store_report.csv");
      return;
    }
  };

  // 🔹 Utility to download
  const triggerDownload = (csvRows: string[], filename: string) => {
    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const isReportReady = reportType && startDate && endDate;

  return (
    <div className="page">
      <Topbar />
      <div className="page-content d-flex align-items-stretch">
        <Sidebar />
        <div className="content-inner">
          <header className="page-header pb-1">
            <div className="container-fluid"><h4>Reports Management</h4></div>
          </header>

          <section className="forms">
            <div className="container-fluid">
              <div className="row">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="form-horizontal mb-3">
                        {/* Report Type */}
                        <div className="row form-group">
                          <label className="col-md-3 col-sm-3 form-control-label font-weight-bold">
                            Report Type <span className="text-danger">*</span>
                          </label>
                          <div className="col-md-9 col-sm-9">
                            <select className="form-control text-capitalize mt-2" value={reportType} onChange={handleInputChange}>
                              <option value="">Select Report Type</option>
                              <optgroup label="Tickets">
                                <option value="tickets">All Tickets</option>
                              </optgroup>
                              {isAdmin && (
                                <optgroup label="Categories">
                                  <option value="ticketsByCategoryAndStore">Tickets by Category and Store</option>
                                </optgroup>
                              )}
                            </select>
                          </div>
                        </div>

                        {reportType === 'ticketsByCategoryAndStore' && (
                          <div className="row">
                            <div className="col">
                              <div className="form-group">
                                <label className="form-control-label font-weight-bold">
                                  Select Stores <span className="text-danger">*</span>
                                </label>
                                <Select
                                  isMulti
                                  value={storeList
                                    .filter((c: any) => selectedStores.includes(c.id))
                                    .map((c: any) => ({
                                      value: c.id,
                                      label: c.name,
                                    }))}
                                  onChange={(selectedOptions) => {
                                    if (!selectedOptions) {
                                      setSelectedStore([]);
                                      return;
                                    }
                                    const hasSelectAll = selectedOptions.some(option => option.value === 'selectAll');
                                    if (hasSelectAll) {
                                      setSelectedStore(storeList.map((c: any) => c.id));
                                    } else {
                                      setSelectedStore(selectedOptions.map(option => option.value));
                                    }
                                  }}
                                  options={[
                                    { value: 'selectAll', label: 'Select All' },
                                    ...storeList.map((c: any) => ({
                                      value: c.id,
                                      label: c.name,
                                    })),
                                  ]}
                                  placeholder={
                                    selectedStores.length === storeList.length
                                      ? 'All Stores Selected'
                                      : 'Select Store'
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Date Inputs */}
                        {(reportType === 'tickets' || reportType === 'ticketsByCategoryAndStore') && (
                          <div className="row">
                            <div className="col">
                              <div className="form-group">
                                <label className="form-control-label font-weight-bold">
                                  Start Date <span className="text-danger">*</span>
                                </label>
                                <input type="datetime-local" name="startDate" max={maxDate} className="form-control" value={startDate} onChange={handleDateChange} />
                              </div>
                            </div>
                            <div className="col">
                              <div className="form-group">
                                <label className="form-control-label font-weight-bold">
                                  End Date <span className="text-danger">*</span>
                                </label>
                                <input type="datetime-local" name="endDate" max={maxDate} className="form-control" value={endDate} onChange={handleDateChange} />
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="form-group float-right mt-3 mb-3 text-end">
                          <button className="btn btn-primary" disabled={!isReportReady} onClick={reportGenerator}>
                            {isLoad ? 'Generating...' : 'Generate Report'}
                          </button>
                        </div>
                      </div>

                      {/* LOADING STATE */}
                      {isLoad && (
                        <div className="alert alert-info" style={{ marginTop: '5rem' }}>
                          <i className="fa fa-spinner fa-spin me-2"></i> Generating report...
                        </div>
                      )}

                      {/* REPORT TABLE — ONLY SHOW IF USER JUST GENERATED IT */}
                      {hasGeneratedReport && (
                        <>
                          {currentTableData.length > 0 ? (
                            <div className="mt-5 pt-4 pb-3">
                              {/* Top Controls */}
                              <div className="row align-items-center mb-3">
                                <div className="col-auto">
                                  <label className="form-label mb-0 me-2 small text-muted">Show:</label>
                                  <select
                                    className="form-select form-select-sm"
                                    value={pageSize}
                                    onChange={(e) => setPageSize(Number(e.target.value))}
                                  >
                                    {[10, 20, 30, 40, 50].map(size => (
                                      <option key={size} value={size}>{size}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="col text-center">
                                  <span className="text-muted fw-medium">
                                    Page <strong>{pageIndex + 1}</strong> of <strong>{getPageCount()}</strong>
                                  </span>
                                </div>
                                <div className="col-auto text-end">
                                  <button className="btn btn-sm btn-success" onClick={downloadCSV}>
                                    <i className="bi bi-download me-1"></i> Download CSV
                                  </button>
                                </div>
                              </div>

                              {/* Table */}
                              <div className="table-responsive">
                                <table className="table table-striped table-bordered">
                                  <thead>
                                    {getHeaderGroups().map(headerGroup => (
                                      <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                          <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            onClick={header.column.getToggleSortingHandler()}
                                            style={{
                                              width: header.getSize(),
                                              fontWeight: header.colSpan > 1 ? 'bold' : 'normal',
                                              backgroundColor: header.colSpan > 1 ? '#f8f9fa' : 'inherit',
                                              borderBottom: header.colSpan > 1 ? '2px solid #dee2e6' : '1px solid #dee2e6',
                                            }}
                                          >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {{
                                              asc: ' 🔼',
                                              desc: ' 🔽',
                                            }[header.column.getIsSorted() as string] ?? null}
                                          </th>
                                        ))}
                                      </tr>
                                    ))}
                                  </thead>
                                  <tbody>
                                    {getRowModel().rows.map(row => (
                                      <tr key={row.id}>
                                        {row.getVisibleCells().map(cell => (
                                          <td key={cell.id} style={{ width: cell.column.getSize() }}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              {/* Pagination */}
                              <div className="d-flex mt-4 px-2">
                                <div className="ms-auto d-flex align-items-center gap-1">
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setPageIndex(0)}
                                    disabled={!getCanPreviousPage()}
                                  >
                                    {'<<'}
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={previousPage}
                                    disabled={!getCanPreviousPage()}
                                  >
                                    {'<'}
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={nextPage}
                                    disabled={!getCanNextPage()}
                                  >
                                    {'>'}
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setPageIndex(getPageCount() - 1)}
                                    disabled={!getCanNextPage()}
                                  >
                                    {'>>'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="alert alert-warning mt-5 pt-4 pb-3">
                              No data found for the selected criteria.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <Footer />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  ticket_report: state.report.ticket_report,
  ticket_cetagory_by_company: state.report.ticket_cetagory_by_company,
});

export default connect(mapStateToProps, { ticketListReport, logoutUser, ticketCetagoryByCompany })(Reports);